/*
 * <abconvert-test-price> - shows an ABConvert price test's price for one variant.
 *
 * Wraps a price the theme has already rendered, so visitors outside a test (and
 * pages without ABConvert) keep that price untouched:
 *
 *   <abconvert-test-price data-variant-id="123" data-base-price="18999" data-amount="18999">
 *     $189.99
 *   </abconvert-test-price>
 *
 * - data-base-price: the variant's own price in cents (variant.price).
 * - data-amount: the amount this element shows, in cents. It can differ from the
 *   base when the theme applied a discount; the discount carries over to the test
 *   price as a percentage (amount / base).
 * - data-role="compare": a struck-through price. It becomes the test price only when
 *   it shows the variant's own price; a real compare-at price is left alone.
 *
 * Uses ABConvert's public JavaScript API (window.ABConvertQueue and
 * getPriceByVariantId), which returns null when this visitor has no test price for
 * the variant. Each element handles itself when it connects, so cards rendered later
 * (filters, mega menu, quick view, section re-renders) are covered too. It only
 * writes when the text changes, because ABConvert watches the page for changes.
 */
if (!customElements.get('abconvert-test-price')) {
  customElements.define(
    'abconvert-test-price',
    class AbconvertTestPrice extends HTMLElement {
      connectedCallback() {
        const amount = Number(this.dataset.amount);
        const basePrice = Number(this.dataset.basePrice);
        if (!this.dataset.variantId || !amount || !basePrice) return;

        window.ABConvertQueue = window.ABConvertQueue || [];
        window.ABConvertQueue.push((ABConvert) => this.applyTestPrice(ABConvert, amount, basePrice));
      }

      applyTestPrice(ABConvert, amount, basePrice) {
        if (!this.isConnected) return;

        try {
          const testPrice = ABConvert.getPriceByVariantId?.(this.dataset.variantId);
          if (!testPrice) return;

          const testCents = Math.round(testPrice.amount * 100);
          if (!testCents || testCents === basePrice) return;

          let cents;
          if (this.dataset.role === 'compare') {
            if (amount !== basePrice) return;
            cents = testCents;
          } else {
            cents = Math.round((amount * testCents) / basePrice);
          }

          const formatted =
            typeof window.formatMoney === 'function'
              ? window.formatMoney(cents)
              : ABConvert.formatPrice(cents / 100, testPrice.currency);
          if (this.innerHTML !== formatted) this.innerHTML = formatted;
        } catch {
          // No test price - the rendered price stays.
        }
      }
    }
  );
}
